import { Injectable } from "@angular/core";
import { IProject } from "@common/interfaces/project";
import { isUserInProjectScope, isUserInScope } from "@common/interfaces/projectScope";
import { Observable, combineLatest } from "rxjs";
import { distinctUntilChanged, map, shareReplay, switchMap, tap } from "rxjs/operators";
import { AuthQuery } from "../auth/auth.query";
import { ProjectQuery } from "./project/project.query";
import { ProjectScopeQuery } from "./projectScope/projectScope.query";
import { UserQuery } from "./user/user.query";
import { UiService } from "./ui/ui.service";
import { UserApiService } from "./user/userApi.service";
import { IUser } from "@common/interfaces/user";

export type EProject = IProject & { isFavorite?: number, canCreateNewTask?: boolean }

@Injectable({
  providedIn: 'root'
})

export class ProjectVisibilityQuery {

  projects$: Observable<EProject[]>;
  allProjectUsers$: Observable<Map<string, IUser[]>>;
  selectFavorites$: Observable<EProject[]>;
  selectFavoritesWithActive$: Observable<EProject[]>;

  constructor(
    private projectQuery: ProjectQuery,
    private projectScopeQuery: ProjectScopeQuery,
    private userQuery: UserQuery,
    private authQuery: AuthQuery,
    private uiService: UiService,
    private userApiService: UserApiService,
  ) {

    this.projects$ = combineLatest([
      this.projectQuery.selectAll(),
      this.projectScopeQuery.selectAll(),
      this.authQuery.user$,
      this.uiService.projectsWhereICanCreateTask$
    ]).pipe(
      map(([projects, _scopes, user, projectsCanCreate]) =>
        projects.reduce((projects: EProject[], project) => {
          const inScope = isUserInScope(user, this.projectScopeQuery.getEntity(project.id));
          if (!inScope) {
            return projects;
          }
          const canCreate = projectsCanCreate.map(p => p.id).includes(project.id);
          projects.push({
            ...project,
            ...(user.settings.favoriteProjects?.includes(project.id) && { isFavorite: 1 }),
            ...canCreate && { canCreateNewTask: true }
          })
          return projects;
        }, []),
        shareReplay({ refCount: true, bufferSize: 1 }),
      ))

    this.selectFavorites$ = this.projects$.pipe(
      map(projects => {
        return projects.filter(p => p.isFavorite === 1);
      }),
      distinctUntilChanged()
    );

    // FV 23.11.07: serve per avere nel menu di sx la lista dei favoriti + del prj aperto che non fosse tra i favoriti
    // questa lista di simboli è ordinato per symbolo ma l'eventuale elemnto che no è tra i favoriti deve essere sempre in fondo
    this.selectFavoritesWithActive$ = combineLatest([this.projects$, this.uiService.query.currentProjectId$]).pipe(
      map(([projects, currentPrjId]) => {
        const prjs = projects.filter(p => !!p.isFavorite || p.id === currentPrjId);
        prjs.sort((a, b) => {
          let rv = 0;
          if (!!a.isFavorite && !b.isFavorite) rv = -1;
          else if (!a.isFavorite && !!b.isFavorite) rv = 1;
          else if (!a.isFavorite && !b.isFavorite) rv = 0;
          else {
            if (a.symbol > b.symbol) rv = 1
            if (a.symbol < b.symbol) rv = -1
          };
          return rv;
        })
        return prjs;
      }),
      distinctUntilChanged()
    );

    this.allProjectUsers$ = combineLatest([
      this.authQuery.user$.pipe(
        tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - AUTH QUERY EMITTED'))
      ),
      this.authQuery.projectRole$.pipe(
        tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - PROJECT ROLE EMITTED'))
      ),
      this.projects$.pipe(
        tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - PROJECTS EMITTED'))
      ),
      this.userQuery.selectAll().pipe(
        tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - USER QUERY EMITTED'))
      ),
    ]).pipe(
      switchMap(
        async ([myUser, projectRole, projects, allUsers]) => {
          let projectUsersMap = new Map<string, IUser[]>();
          for (let p of projects) {
            const myRoles = projectRole[p.id];
            if (!myRoles) {
              projectUsersMap.set(p.id, []);
            }
            else {
              const scope = this.projectScopeQuery.getByProjectId(p.id);
              let usersInScope: IUser[] = allUsers.filter(user => isUserInProjectScope(user, scope));
              if (myRoles.includes('CompanyLimited')) {
                usersInScope = usersInScope.filter(user => user.companyId === myUser.companyId);
              } else if (myRoles.includes('OloNetworkLimited')) {
                usersInScope = await this.computeUsersInScopeForOloNetLimited(usersInScope);
              }
              projectUsersMap.set(p.id, usersInScope);
            }
          }
          return projectUsersMap;
        }
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  async computeUsersInScopeForOloNetLimited(usersInScope: IUser[]) {
    const myUserOloNetworks = this.authQuery.getLoggedUser()?.oloNetworks;
    if (!!myUserOloNetworks && myUserOloNetworks.length > 0) {
      const usersInScopeIdsAndOloNetworks = await this.userApiService.getOloNetworksByUsersIds(usersInScope.map(u => u.id)).toPromise();
      usersInScope = usersInScope.filter(u => myUserOloNetworks.some(
        myUserOloNet => (usersInScopeIdsAndOloNetworks[u.id] ?? []).some(
          userInScopeOloNet => userInScopeOloNet.olo === myUserOloNet.olo && userInScopeOloNet.network === myUserOloNet.network
        )
      ));
    }
    return usersInScope;
  }

  getProjectScops() {
    return this.projectScopeQuery.getAll();
  }

  getActiveProjects() {
    const user = this.authQuery.getLoggedUser();
    console.log('active project user -> ', user);
    return this.projectQuery.getAll().reduce((projects: EProject[], project) => {
      const inScope = isUserInScope(user, this.projectScopeQuery.getEntity(project.id));
      if (!inScope) {
        return projects;
      }
      projects.push({
        ...project,
        ...(user?.settings?.favoriteProjects?.includes(project.id) && { isFavorite: 1 }),
      })
      return projects;
    }, []);
  }

  activeProjectUsers$ = combineLatest([
    this.authQuery.user$.pipe(
      tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - AUTH QUERY EMITTED'))
    ),
    this.authQuery.projectRole$.pipe(
      tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - PROJECT ROLE EMITTED'))
    ),
    this.projectScopeQuery.active$.pipe(
      tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - ACTIVE PROJECT SCOPE EMITTED'))
    ),
    this.userQuery.selectAll().pipe(
      tap(_ => console.log('[PROJECT_VISIBLITY_QUERY] - USER QUERY EMITTED'))
    ),
  ]).pipe(
    switchMap(
      async ([myUser, projectRole, scope, allUsers]) => {
        console.log(scope);
        if (!scope) {
          return;
        }
        const myRoles = projectRole[scope.projectId];
        if (!myRoles) {
          return;
        }
        let usersInScope = allUsers.filter(user => isUserInProjectScope(user, scope));
        if (myRoles.includes('CompanyLimited')) {
          return usersInScope.filter(user => user.companyId === myUser.companyId);
        } else if (myRoles.includes('OloNetworkLimited')) {
          usersInScope = await this.computeUsersInScopeForOloNetLimited(usersInScope);
        }
        return usersInScope;
      }
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
}
